import { useCallback, useEffect, useState } from 'react';
import { useListingStore } from '@brightmls/nextgen.lae.flc';
import { useDraftListing } from '../../../contexts/DraftListingContext';
import { ListingResource, PropertyField } from '../../../system/field';
import {
  ListingStateKey,
  ListingMetricType,
  ListingState as AssistantState,
} from '../assistant/utils/types';

export interface ReadyToImportField {
  key: string;
  value: any;
  source: string;
  isInContext: boolean;
  contextValue?: any;
}

export interface NeedsReviewField {
  key: string;
  name: string;
  hasConflict: boolean;
  options: Array<{
    label: string;
    value: any;
    recordId: string;
  }>;
  resolvedValue?: any;
  currentContextValue?: any;
  isResolved: boolean;
  autoResolved?: boolean;
}

export interface DebugData {
  listingContext: {
    fields: Record<string, any>;
    count: number;
    listingKey?: string;
    listingId?: string;
  };
  draftListingState: {
    hasAssistant: boolean;
    hasLocation: boolean;
    isLoadingSuggestions: boolean;
    suggestionsError?: string;
    recordsCount: number;
    recordTypes: string[];
  };
  listingState: {
    published: boolean;
    locked: boolean;
    discarded: boolean;
    isFromLiveListing: boolean;
    focusedFieldsCount: number;
    hiddenFieldsCount: number;
  };
  statusFlags: {
    ready: boolean;
    touched: boolean;
    invalidPhone: boolean;
    noTaxRecordMode: boolean;
  };
  readyToImport: {
    fields: ReadyToImportField[];
    count: number;
    savedCount: number;
    pendingCount: number;
  };
  needsReview: {
    fields: NeedsReviewField[];
    totalCount: number;
    resolvedCount: number;
    pendingCount: number;
  };
  listingAssistant: {
    lastSaveTime?: Date;
    changesSinceSave: boolean;
    enabledRecords: Array<{
      id: string;
      type: string;
      label: string;
      isEnabled: boolean;
    }>;
    metrics: Record<string, number>;
  };
}

interface UseDebugPanelProps {
  assistantState: AssistantState;
}

export const useDebugPanel = ({ assistantState }: UseDebugPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | undefined>();

  // Get draft listing context (this stays the same)
  const { assistant, location, isLoadingSuggestions, suggestionsError } =
    useDraftListing();

  // Use direct store access for debug panel since we need comprehensive state
  const listingStore = useListingStore();

  // Track when saveFields is called by monitoring the listing context changes
  useEffect(() => {
    const handleFieldUpdate = () => {
      // When fields are updated, check if it's from a save operation
      const touched = listingStore.listingStatus?.touched || false;
      if (!touched && lastSaveTime) {
        // If touched is false after having a lastSaveTime, it means save was just clicked
        setLastSaveTime(new Date());
      }
    };

    window.addEventListener('field-update', handleFieldUpdate);
    return () => window.removeEventListener('field-update', handleFieldUpdate);
  }, [listingStore.listingStatus?.touched, lastSaveTime]);

  // Monitor touched status changes
  useEffect(() => {
    const touched = listingStore.listingStatus?.touched || false;
    if (!touched && assistant) {
      // When touched becomes false, it usually means save was clicked
      setLastSaveTime(new Date());
    }
  }, [listingStore.listingStatus?.touched, assistant]);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const getDebugData = useCallback((): DebugData => {
    // Access the full store state
    const propertyData = listingStore[ListingResource.Property] || {};
    const listingStatus = listingStore.listingStatus || {};

    // Get current listing context values
    const nonEmptyFields = Object.entries(propertyData)
      .filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      )
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Get record types from assistant data
    const recordTypes = assistant?.records
      ? [...new Set(assistant.records.map((r: any) => r.Type))]
      : [];

    // Get ready to import fields with more detail
    const readyToImportList =
      assistantState[ListingStateKey.READY_TO_IMPORT_LIST];
    const readyToImportFields: ReadyToImportField[] = Object.entries(
      readyToImportList.fields || {},
    )
      .filter(
        ([_, field]: [string, any]) => field.readyToImportValues?.hasValues,
      )
      .map(([key, field]: [string, any]) => {
        const importValue = Object.values(
          field.readyToImportValues.added || {},
        )[0] as any;
        const contextValue = propertyData[key];
        const isInContext =
          contextValue !== undefined &&
          contextValue !== null &&
          contextValue !== '';

        return {
          key,
          value: importValue?.value,
          source: importValue?.label || 'Unknown',
          isInContext,
          contextValue: isInContext ? contextValue : undefined,
        };
      });

    const savedCount = readyToImportFields.filter((f) => f.isInContext).length;
    const pendingCount = readyToImportFields.filter(
      (f) => !f.isInContext,
    ).length;

    // Get needs review fields
    const templateFieldList =
      assistantState[ListingStateKey.TEMPLATE_FIELDLIST];
    const resolvedFields = assistantState[ListingStateKey.RESOLVED_FIELDS];

    const needsReviewFields: NeedsReviewField[] = Object.values(
      templateFieldList.fields || {},
    )
      .filter(
        (field: any) =>
          field.reviewProps?.hasConflictData ||
          resolvedFields[field.key] ||
          field.reviewProps?.autoResolved,
      )
      .map((field: any) => {
        const resolved = resolvedFields[field.key];
        const currentValue = propertyData[field.key];

        return {
          key: field.key,
          name: field.metadata?.Name || field.key,
          hasConflict: field.reviewProps?.hasConflictData || false,
          options: (field.reviewProps?.optionsToShow || []).map((opt: any) => ({
            label: opt.label || 'Unknown',
            value: opt.value,
            recordId: opt.recordId,
          })),
          resolvedValue: resolved?.value,
          currentContextValue: currentValue,
          isResolved: !!resolved || field.reviewProps?.autoResolved,
          autoResolved: field.reviewProps?.autoResolved || false,
        };
      });

    const needsReviewPending = needsReviewFields.filter(
      (f) => f.hasConflict && !f.isResolved,
    );
    const needsReviewResolved = needsReviewFields.filter((f) => f.isResolved);

    // Get enabled records
    const records = assistantState[ListingStateKey.RECORDS] || [];
    const enabledRecords =
      assistantState[ListingStateKey.ENABLED_RECORDS] || {};
    const recordsData = records.map((record: any) => {
      const enabled = enabledRecords[record.id] || {};
      return {
        id: record.id,
        type: record.Type,
        label: enabled.label || `${record.Year} ${record.Type}`,
        isEnabled: enabled.isEnabled || false,
      };
    });

    // Get metrics
    const metrics = assistantState[ListingStateKey.METRICS] || {};

    // Check if there are changes since last save
    const changesSinceSave = listingStatus.touched || false;

    return {
      listingContext: {
        fields: nonEmptyFields,
        count: Object.keys(nonEmptyFields).length,
        listingKey: propertyData[PropertyField.LISTING_KEY],
        listingId: propertyData[PropertyField.LISTING_ID],
      },
      draftListingState: {
        hasAssistant: !!assistant,
        hasLocation: !!location,
        isLoadingSuggestions,
        suggestionsError: suggestionsError?.message,
        recordsCount: assistant?.records?.length || 0,
        recordTypes,
      },
      listingState: {
        published: listingStore.published,
        locked: listingStore.locked,
        discarded: listingStore.discarded,
        isFromLiveListing: listingStore.isFromLiveListing,
        focusedFieldsCount: listingStore.focusedFields?.size || 0,
        hiddenFieldsCount: listingStore.hiddenFields?.size || 0,
      },
      statusFlags: {
        ready: listingStatus.ready || false,
        touched: listingStatus.touched || false,
        invalidPhone: listingStatus.invalidPhone || false,
        noTaxRecordMode: listingStatus.no_tax_record_mode || false,
      },
      readyToImport: {
        fields: readyToImportFields,
        count: readyToImportFields.length,
        savedCount,
        pendingCount,
      },
      needsReview: {
        fields: needsReviewFields,
        totalCount: needsReviewFields.length,
        resolvedCount: needsReviewResolved.length,
        pendingCount: needsReviewPending.length,
      },
      listingAssistant: {
        lastSaveTime,
        changesSinceSave,
        enabledRecords: recordsData,
        metrics: {
          [ListingMetricType.NEEDS_REVIEW]:
            metrics[ListingMetricType.NEEDS_REVIEW] || 0,
          [ListingMetricType.READY_TO_IMPORT]:
            metrics[ListingMetricType.READY_TO_IMPORT] || 0,
          [ListingMetricType.NO_CONFLICT_FIELDS]:
            metrics[ListingMetricType.NO_CONFLICT_FIELDS] || 0,
          [ListingMetricType.REMAINING_REVIEW_FIELDS]:
            metrics[ListingMetricType.REMAINING_REVIEW_FIELDS] || 0,
          [ListingMetricType.SOLVED_BY_USER]:
            metrics[ListingMetricType.SOLVED_BY_USER] || 0,
        },
      },
    };
  }, [
    assistant,
    location,
    isLoadingSuggestions,
    suggestionsError,
    assistantState,
    lastSaveTime,
    listingStore,
  ]);

  return {
    isOpen,
    togglePanel,
    closePanel,
    getDebugData,
  };
};
